import A2O from 'utils/A2O';
import axios from 'axios';
import { checkTimeOutAccessToken } from 'utils/accessAuth';
import { notification } from 'antd';
import { appApi, s3Api } from 'configs/variables';

const SECONDS = 1000;
const timeout = 60 * SECONDS;

let refreshTokenStatus = false;

export const service = {
  local: axios.create({
    baseURL: appApi,
    timeout,
  }),
  main: axios.create({
    baseURL: `${appApi}/cases`,
    timeout,
  }),
  master_indices: axios.create({
    baseURL: `${appApi}/master-indices`,
    timeout,
  }),
  incident_report: axios.create({
    baseURL: `${appApi}/reporting`,
    timeout,
  }),
  one_command: axios.create({
    baseURL: `${appApi}/webview`,
    timeout,
  }),
  system_admin: axios.create({
    baseURL: `${appApi}/system-admin`,
    timeout,
  }),
  personnel: axios.create({
    baseURL: `${appApi}/personnel`,
    timeout,
  }),
  upload: axios.create({
    baseURL: s3Api,
    timeout,
  }),
  notification: axios.create({
    baseURL: `${appApi}/notification`,
    timeout,
  }),
  authentication: axios.create({
    baseURL: `${appApi}/auth`,
    timeout,
  }),
  evidence: axios.create({
    baseURL: `${appApi}/evidence`,
    timeout,
  }),
  advanceSearch: axios.create({
    baseURL: `${appApi}/search`,
    timeout,
  }),
  log: axios.create({
    baseURL: `${appApi}/log`,
    timeout,
  }),
  gis: axios.create({
    baseURL: `${appApi}/gis`,
    timeout,
  }),
  patrol: axios.create({
    baseURL: `${appApi}/patrol`,
    timeout,
  }),
  alarm_monitoring: axios.create({
    baseURL: `${appApi}/alarm-monitoring/v1`,
    timeout,
  }),
  user: axios.create({
    baseURL: `${appApi}/user`,
    timeout,
  }),
};

const RequestInterceptor = {
  _config: (config) => {
    const { method, data, authorized = {}, headersKey = 'Authorization' } = config;
    const { enable = true, key_name = 'id_token' } = authorized;

    const getParse = localStorage.getItem('user-store') || null;
    const { accessAuthen = {} } = JSON.parse(getParse);
    const { id_token = '', access_token = '' } = accessAuthen;

    const accessType = {
      id_token,
      access_token,
    };

    if (enable) {
      config.headers[headersKey] = accessType[key_name];
    }

    const newConfig = {
      ...config,
      data: ['post', 'put'].includes(method) && data ? A2O.TRIMNULL(data) : data,
    };

    return newConfig;
  },
  _error: (error) => {
    notification.error({
      message: 'Error',
    });
    Promise.reject(error);
  },
};

const ResponseInterceptor = {
  _response: (response) => {
    return response.data;
  },
  _error: async (error) => {
    refreshTokenStatus = await checkTimeOutAccessToken(error, refreshTokenStatus);
    return Promise.reject(error?.response?.data);
  },
};

// API Request interceptor
service.main.interceptors.request.use(
  (config) => RequestInterceptor._config(config),
  (error) => RequestInterceptor._error(error)
);
service.master_indices.interceptors.request.use(
  (config) => RequestInterceptor._config(config),
  (error) => RequestInterceptor._error(error)
);
service.incident_report.interceptors.request.use(
  (config) => RequestInterceptor._config(config),
  (error) => RequestInterceptor._error(error)
);
service.one_command.interceptors.request.use(
  (config) => RequestInterceptor._config(config),
  (error) => RequestInterceptor._error(error)
);
service.personnel.interceptors.request.use(
  (config) => RequestInterceptor._config(config),
  (error) => RequestInterceptor._error(error)
);
service.upload.interceptors.request.use(
  (config) => RequestInterceptor._config(config),
  (error) => RequestInterceptor._error(error)
);
service.notification.interceptors.request.use(
  (config) => RequestInterceptor._config(config),
  (error) => RequestInterceptor._error(error)
);
service.authentication.interceptors.request.use(
  (config) =>
    RequestInterceptor._config({
      ...config,
      authorized: {
        enable: false,
        key_name: 'id_token',
      },
    }),
  (error) => RequestInterceptor._error(error)
);
service.evidence.interceptors.request.use(
  (config) => RequestInterceptor._config(config),
  (error) => RequestInterceptor._error(error)
);
service.advanceSearch.interceptors.request.use(
  (config) => RequestInterceptor._config(config),
  (error) => RequestInterceptor._error(error)
);
service.log.interceptors.request.use(
  (config) => RequestInterceptor._config(config),
  (error) => RequestInterceptor._error(error)
);
service.gis.interceptors.request.use(
  (config) => RequestInterceptor._config(config),
  (error) => RequestInterceptor._error(error)
);
service.patrol.interceptors.request.use(
  (config) => RequestInterceptor._config(config),
  (error) => RequestInterceptor._error(error)
);
service.alarm_monitoring.interceptors.request.use(
  (config) =>
    RequestInterceptor._config({
      ...config,
      authorized: {
        key_name: 'access_token',
      },
    }),
  (error) => RequestInterceptor._error(error)
);
service.user.interceptors.request.use(
  (config) =>
    RequestInterceptor._config({
      ...config,
      authorized: {
        key_name: 'access_token',
      },
    }),
  (error) => RequestInterceptor._error(error)
);
service.system_admin.interceptors.request.use(
  (config) =>
    RequestInterceptor._config({
      ...config,
      authorized: {
        key_name: 'access_token',
      },
    }),
  (error) => RequestInterceptor._error(error)
);

// API respone interceptor
service.main.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.master_indices.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.incident_report.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.one_command.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.system_admin.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.personnel.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.upload.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.notification.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.evidence.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.authentication.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.advanceSearch.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.log.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.gis.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.patrol.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.alarm_monitoring.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);
service.user.interceptors.response.use(
  (response) => ResponseInterceptor._response(response),
  (error) => ResponseInterceptor._error(error)
);

export default service;
